/**
 * 额度 / 授权提示（全局）
 * ============================================================
 *
 * 任何接口被服务端拒绝（402 / 429）都会派发 `dg:quota-blocked` 事件，
 * 由这个组件统一接住并显示。
 *
 * 为什么不放在各个页面里：
 *   花钱的接口散落在课堂、对话、翻译、生成器……一处漏处理，
 *   用户看到的就是"点了没反应"或者一句莫名的"请求失败"。
 */

import { useCallback, useEffect, useState } from 'react';
import { redeem, fetchQuota, describeStatus } from '../lib/license';

const COPY = {
  EXPIRED: {
    title: '试用已结束',
    body: '输入卡密即可继续使用 AI 对话、翻译和课程生成。已有的课程与口型片段不受影响，仍然可以正常学习。',
    canRedeem: true,
  },
  QUOTA_EXCEEDED: {
    title: '今天的额度用完了',
    body: '额度每天零点（北京时间）重置。已经学过的课程不受影响；如果不想等，输入卡密可以立刻恢复。',
    canRedeem: true,
  },
  RATE_LIMITED: {
    title: '操作太频繁了',
    body: '稍等一会儿再试。这是为了防止误触和脚本刷接口，正常情况下不该频繁出现。',
    canRedeem: false,
  },
  BLOCKED: {
    title: '设备已停用',
    body: '这台设备已被停用。如果是误判，请联系客服处理。',
    canRedeem: false,
  },
  NO_DEVICE: {
    title: '需要重新打开应用',
    body: '设备信息丢失了。请完全退出后重新打开 App 再试。',
    canRedeem: false,
  },
  /*
   * 主动输入卡密（不是被 402 拦下来的）。
   * 从「我的」页的授权卡片触发：试用期想提前升级、或已激活想续期/换卡，
   * 都走这里。和"被拒后弹"共用同一个弹层，避免两套输入框不一致。
   */
  MANUAL: {
    title: '输入卡密',
    body: '输入卡密可升级或续期。到期前激活会在剩余基础上叠加；额度不足时可立即恢复。',
    canRedeem: true,
  },
};

export default function LicenseGate() {
  const [block, setBlock] = useState(null); // { code, message, quota }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, message }

  useEffect(() => {
    const onBlocked = (e) => {
      setBlock(e.detail || {});
      setResult(null);
      setCode('');
    };
    window.addEventListener('dg:quota-blocked', onBlocked);
    return () => window.removeEventListener('dg:quota-blocked', onBlocked);
  }, []);

  const close = useCallback(() => {
    setBlock(null);
    setResult(null);
    setCode('');
  }, []);

  const submit = useCallback(async () => {
    const v = code.trim();
    if (!v || busy) return;
    setBusy(true);
    setResult(null);
    const r = await redeem(v);
    setBusy(false);
    setResult({ ok: r.ok, message: r.message });

    if (r.ok) {
      // 激活成功：刷新一次状态，1.2 秒后自动关掉，让用户看到"成功"再消失
      fetchQuota().catch(() => {});
      setTimeout(close, 1200);
    }
  }, [code, busy, close]);

  if (!block) return null;

  const copy = COPY[block.code] || {
    title: '暂时无法使用',
    body: block.message || '请稍后重试。',
    canRedeem: block.code === 'EXPIRED' || block.code === 'QUOTA_EXCEEDED',
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 px-4 pb-6 sm:items-center sm:pb-0">
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl dark:bg-zinc-800">
        <div className="flex items-start gap-3">
          <span className="material-icons !text-2xl text-amber-500">info</span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-zinc-800 dark:text-zinc-100">{copy.title}</p>
            <p className="mt-1 text-[12px] leading-relaxed text-zinc-500 dark:text-zinc-400">
              {copy.body}
            </p>
          </div>
        </div>

        {block.quota && (
          <div className="mt-3 flex gap-3 rounded-xl bg-zinc-50 px-3 py-2 text-[11px] text-zinc-500 dark:bg-zinc-900/40">
            <span>
              今日剩余 <b className="text-zinc-700 dark:text-zinc-200">{block.quota.remaining}</b>{' '}
              / {block.quota.limit} 点
            </span>
            {block.quota.trialDaysLeft != null && (
              <span>试用剩 {block.quota.trialDaysLeft} 天</span>
            )}
          </div>
        )}

        {copy.canRedeem && (
          <div className="mt-4 space-y-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="输入卡密，如 DG-7K2M-9XQP-4TWD"
              className="w-full rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 text-sm tracking-wide text-zinc-800 outline-none focus:border-teal-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              autoComplete="off"
              spellCheck={false}
            />
            <button
              type="button"
              onClick={submit}
              disabled={!code.trim() || busy}
              className="w-full rounded-xl bg-teal-500 py-2.5 text-sm font-bold text-white disabled:opacity-40"
            >
              {busy ? '激活中…' : '激活'}
            </button>
            {result && (
              <p
                className={`text-[11px] ${
                  result.ok ? 'text-emerald-600' : 'text-rose-500'
                }`}
              >
                {result.message}
              </p>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={close}
          className="mt-3 w-full rounded-xl bg-zinc-100 py-2 text-xs font-bold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
        >
          知道了
        </button>
      </div>
    </div>
  );
}
